import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Table,
  Header,
  Button,
  Checkbox,
  Modal,
  Dropdown,
  Form,
  Pagination,
  Grid,
} from 'semantic-ui-react';
import { useStore } from '../../app/stores/store';
import { toast } from 'react-toastify';

function ShiftRoster() {
  const { ADLtrackingStore, userStore } = useStore();
  const { user } = userStore;
  const { shiftRosterList, shiftRosterCount, loadShiftDetails, getEmployeesUnderLead, empIds } = ADLtrackingStore;
  const currentUserName = user?.username;
  const currentEmpID = user?.displayName as string;
  const [shiftOptions, setShiftOptions] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignEmpId, setAssignEmpId] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [shiftStartDate, setShiftStartDate] = useState('');
  const [shiftEndDate, setShiftEndDate] = useState('');
  const [selectedShift, setSelectedShift] = useState('');
  const [activePage, setActivePage] = useState(1);
  const pageSize = 15;

  useEffect(() => {
    getEmployeesUnderLead(currentEmpID);
    loadShiftDetails(currentEmpID, activePage, pageSize);
  // eslint-disable-next-line
  }, [activePage, currentEmpID]);

  useEffect(() => {
    const fetchShiftOptions = async () => {
      try {
        const shiftList = await ADLtrackingStore.getAllshiftDetails();
        const options = shiftList.map((shift: any) => ({
          key: shift.shiftCode,
          value: shift.shiftCode,
          text: `${shift.shiftName} (${shift.inTime} - ${shift.outTime})`,
        }));
        setShiftOptions(options);
      } catch (err) {
        toast.error('Failed to load shift details');
      }
    };

    fetchShiftOptions();
  }, [ADLtrackingStore]);

  const handleSelectRow = (empId: string) => {
    setSelectedRows((prev) =>
      prev.includes(empId) ? prev.filter((id) => id !== empId) : [...prev, empId]
    );
  };

  const handleSelectAll = () => {
    const allIds = shiftRosterList.map((d) => d.employeeId);
    setSelectedRows(selectAll ? [] : allIds);
    setSelectAll(!selectAll);
  };

  const handleAssignShift = async () => {
    if (!assignEmpId || !shiftStartDate || !shiftEndDate || !selectedShift) {
      toast.error('Please fill all fields.');
      return;
    }

    try {
      const payloads = (Array.isArray(assignEmpId) ? assignEmpId : [assignEmpId]).map((empId: string) => ({
        employeeId: empId,
        shiftCode: selectedShift,
        startDate: shiftStartDate,
        endDate: shiftEndDate
      }));
      await ADLtrackingStore.addShiftRoaster(payloads);
      toast.success('Shift assigned successfully.');
      setAssignModalOpen(false);
      setAssignEmpId([]);
      setShiftStartDate('');
      setShiftEndDate('');
      setSelectedShift('');
      await loadShiftDetails(currentEmpID, activePage, pageSize);
    } catch (error) {
      console.error(error);
      toast.error('Failed to assign shift.');
    }
  }

  const handleUpdateShift = async () => {
    if (!shiftStartDate || !shiftEndDate || !selectedShift) {
      toast.error('Please fill all fields.');
      return;
    }

    try {
      const updatedDate = new Date().toISOString();
      const updatedBy = currentUserName || 'system';

      const adlShiftUser = shiftRosterList
        .filter((item) => selectedRows.includes(item.employeeId))
        .map((item) => ({
          empId: item.employeeId,
          date: item.startDate,
          oldShiftCode: item.shiftCode,
        }));

      const payload = {
        shiftStartDate,
        shiftEndDate,
        newShiftCode: selectedShift,
        updatedBy,
        updatedDate,
        adlShiftUser,
      };

      await ADLtrackingStore.updateShiftRoster(payload);

      toast.success('Shift updated successfully.');
      setModalOpen(false);
      setShiftStartDate('');
      setShiftEndDate('');
      setSelectedShift('');
      setSelectedRows([]);
      setSelectAll(false);
      await loadShiftDetails(currentEmpID, activePage, pageSize);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update shift.');
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setAssignModalOpen(false);
    setAssignEmpId([]);
    setShiftStartDate('');
    setShiftEndDate('');
    setSelectedShift('');
    setSelectedRows([]);
    setSelectAll(false);
  };

  const handlePageChange = (e: any, { activePage }: any) => {
    setActivePage(activePage);
    setSelectedRows([]);
    setSelectAll(false);
  };

  const totalPages = Math.ceil(shiftRosterCount / pageSize);

  return (
    <div className="title-wise-table">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Header>Shift Roster</Header>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button
            color="red"
            inverted
            onClick={() => setAssignModalOpen(true)}
          >
            Assign Shift
          </Button>
          <Button
            color="blue"
            inverted
            onClick={() => setModalOpen(true)}
            disabled={selectedRows.length === 0}
          >
            Update Shift
          </Button>
        </div>
      </div>

      <Table celled selectable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell collapsing>
              <Checkbox checked={selectAll} onChange={handleSelectAll} />
            </Table.HeaderCell>
            <Table.HeaderCell>Employee ID</Table.HeaderCell>
            <Table.HeaderCell>Employee Name</Table.HeaderCell>
            <Table.HeaderCell>Start Date</Table.HeaderCell>
            <Table.HeaderCell>End Date</Table.HeaderCell>
            <Table.HeaderCell>Shift Code</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {shiftRosterList.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan="6" textAlign="center">
                No Shift Data Available
              </Table.Cell>
            </Table.Row>
          ) : (
            shiftRosterList.map((row, idx) => {
              const isSelected = selectedRows.includes(row.employeeId);
              return (
                <Table.Row
                  key={idx}
                  onClick={() => handleSelectRow(row.employeeId)}
                  style={{ backgroundColor: isSelected ? '#f0f8ff' : 'inherit', cursor: 'pointer' }}
                >
                  <Table.Cell collapsing onClick={(e:any) => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleSelectRow(row.employeeId)}
                    />
                  </Table.Cell>
                  <Table.Cell>{row.employeeId}</Table.Cell>
                  <Table.Cell>{row.employeeName}</Table.Cell>
                  <Table.Cell>{row.startDate?.split('T')[0]}</Table.Cell>
                  <Table.Cell>{row.endDate?.split('T')[0]}</Table.Cell>
                  <Table.Cell>{row.shiftCode}</Table.Cell>
                </Table.Row>
              );
            })
          )}
        </Table.Body>
      </Table>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
        <Pagination
          activePage={activePage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          boundaryRange={1}
          siblingRange={1}
        />
      </div>

      <Modal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        size="large"
      >
        <Modal.Header>Assign Shift</Modal.Header>
        <Modal.Content>
          <Form>
            <Grid columns={2} stackable>
              <Grid.Row>
                <Grid.Column stretched>
                  <Form.Field>
                    <label>Employee ID</label>
                    <Dropdown
                      fluid
                      selection
                      multiple
                      clearable
                      options={empIds}
                      value={assignEmpId}
                      onChange={(e, { value }) => setAssignEmpId(value as string[])}
                      placeholder="Select Employee ID"
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Form.Input
                    label="Shift Start Date"
                    type="date"
                    value={shiftStartDate}
                    onChange={(e) => setShiftStartDate(e.target.value)}
                  />
                </Grid.Column>

                <Grid.Column>
                  <Form.Input
                    label="Shift End Date"
                    type="date"
                    value={shiftEndDate}
                    onChange={(e) => setShiftEndDate(e.target.value)}
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row stretched>
                <Grid.Column>
                  <Form.Field>
                    <label>Select Shift</label>
                    <Dropdown
                      fluid
                      selection
                      clearable
                      options={shiftOptions}
                      value={selectedShift}
                      onChange={(e, { value }) => setSelectedShift(value as string)}
                      placeholder="Choose a shift"
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={16} textAlign="right">
                  <Button.Group style={{ gap: '5px' }}>
                    <Button
                      type="submit"
                      color="green"
                      onClick={handleAssignShift}
                      disabled={
                        !assignEmpId ||
                        !shiftStartDate ||
                        !shiftEndDate ||
                        !selectedShift
                      }
                    >
                      Submit
                    </Button>
                    <Button type="reset" color="red" onClick={handleCloseModal}>
                      Cancel
                    </Button>
                  </Button.Group>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        </Modal.Content>
      </Modal>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        size="large"
      >
        <Modal.Header>Update Shift</Modal.Header>
        <Modal.Content>
          <Form>
            <Grid columns={2} stackable>
              <Grid.Row>
                <Grid.Column>
                  <Form.Input
                    label="Shift Start Date"
                    type="date"
                    value={shiftStartDate}
                    onChange={(e) => setShiftStartDate(e.target.value)}
                  />
                </Grid.Column>

                <Grid.Column>
                  <Form.Input
                    label="Shift End Date"
                    type="date"
                    value={shiftEndDate}
                    onChange={(e) => setShiftEndDate(e.target.value)}
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={16}>
                  <Form.Field>
                    <label>Select Shift</label>
                    <Dropdown
                      fluid
                      selection
                      options={shiftOptions}
                      value={selectedShift}
                      onChange={(e, { value }) => setSelectedShift(value as string)}
                      placeholder="Choose a shift"
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={16} textAlign="right">
                  <Button.Group style={{ gap: '5px' }}>
                    <Button
                      type="submit"
                      color="green"
                      onClick={handleUpdateShift}
                      disabled={!shiftStartDate || !shiftEndDate || !selectedShift}
                    >
                      Submit
                    </Button>
                    <Button
                      type="reset"
                      color="red"
                      onClick={handleCloseModal}
                    >
                      Cancel
                    </Button>
                  </Button.Group>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        </Modal.Content>
      </Modal>
    </div>
  );
}

export default observer(ShiftRoster);
